<i18n src="@/locales/models/harvest_team_work_log.yml" lang="yaml"></i18n>
<i18n
  src="@/locales/views/organization/process/work_logs/new_harvest_team_work_log.yml"
  lang="yaml"
></i18n>

<template>
  <div>
    <go-back-link
      :text="$t('labels.go_back_link')"
      :name="routeName('show_process')"
      :params="{
        organization_id: organization_id,
        harvest_process_id: harvest_process_id,
      }"
    />
    <bread-crumbs :labels="[module_name, harvest_process.name]"></bread-crumbs>

    <alert-danger :errors_data="std_err" />

    <div class="mt-8" v-if="load_general_data">
      <div class="panel">
        <div class="panel-header">
          <h3 class="panel-header-title">
            {{ $t("labels.title") }}
          </h3>
        </div>

        <div class="panel-body mb-2 md:mb-0">
          <div class="space-y-8 divide-y divide-slate-200">
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvest_team_work_log.work_date") }} (DD/MM/YYYY)
                </label>
                <div class="mt-1">
                  <date-form
                    v-model="harvest_team_work_log.work_date"
                    :readonly="false"
                  />
                </div>
              </div>
            </div>
            <div class="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6 pt-4">
              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvest_team_work_log.worker_count") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="harvest_team_work_log.worker_count"
                    :class="['input-text', isInvalid('worker_count')]"
                  />
                </div>
              </div>

              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvest_team_work_log.worked_hours") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="harvest_team_work_log.worked_hours"
                    :class="['input-text', isInvalid('worked_hours')]"
                  />
                </div>
              </div>

              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvest_team_work_log.productivity_ratio") }}
                </label>
                <div class="mt-1">
                  <input
                    type="text"
                    v-model.trim="harvest_team_work_log.productivity_ratio"
                    :class="['input-text', isInvalid('productivity_ratio')]"
                  />
                </div>
              </div>

              <div class="sm:col-span-3">
                <label class="label-form">
                  {{ $t("model.harvest_team_work_log.harvest_team") }}
                </label>
                <div class="mt-1">
                  <select-form
                    v-if="harvest_teams.length > 0"
                    :options="harvest_teams"
                    v-model="harvest_team_work_log.harvest_team_id"
                    :is_invalid="isInvalidSelect('harvest_team')"
                    :clearable="false"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="panel-footer">
          <div class="panel-footer-actions space-y-3 my-3 md:space-y-0 md:my-0">
            <white-button
              @click.prevent="goToShowProcess()"
              :label="$t('labels.cancel_button')"
            />
            <span class="space-actions"></span>
            <success-button
              v-if="!charging_submit"
              @click.prevent="update()"
              :label="$t('labels.save_button')"
            />
            <success-button-processing
              v-else
              :label="$t('labels.save_button')"
            />
          </div>
        </div>
      </div>
    </div>
    <loading-message v-else />
  </div>
</template>

<script>
import Requester from "@/views/organization/requesters/process/work_logs/manage_harvest_team_work_log.js";

export default {
  name: "OrgProcessWorkLogNewHarvestTeamWorkLog",
  data() {
    return {
      load_general_data: false,
      charging_submit: false,
      harvest_process: {},
      harvest_team_work_log: {
        worker_count: null,
        worked_hourse: null,
        worked_at: null,
        harvest_team_id: null,
        productivity_ratio: null,
      },
      harvest_teams: [],
    };
  },
  props: {
    organization_id: { required: true },
    harvest_process_id: { required: true },
    harvest_team_work_log_id: { required: true },
  },
  created() {
    this.checkIfAuthenticated().then(() => {
      this.checkIfAuthorized({
        role: this.$route.meta.role,
        permission: this.$route.meta.permission,
        entity: {
          organization_id: this.organization_id,
        },
      }).then(() => {
        this.loadData();
      });
    });
  },

  methods: {
    loadData: async function () {
      await this.fetchHarvestProcess();
      await this.fetchHarvestTeamWorkLog();
      await this.fetchHarvestTeams();
      this.load_general_data = true;
    },
    fetchHarvestProcess: async function () {
      const response = await Requester.fetchHarvestProcess(
        this.organization_id,
        this.harvest_process_id
      );
      this.harvest_process = response.data;
    },

    fetchHarvestTeamWorkLog: async function () {
      const response = await Requester.fetchHarvestTeamWorkLog(
        this.organization_id,
        this.harvest_process_id,
        this.harvest_team_work_log_id
      );
      this.harvest_team_work_log = response.data;
    },

    fetchHarvestTeams: async function () {
      const response = await Requester.fetchHarvestTeams(
        this.organization_id,
        this.harvest_process_id
      );
      this.harvest_teams = response.data;
      this.harvest_teams.forEach((team) => {
        team["name"] = team.harvest_team_code;
      });
    },

    update: async function () {
      this.charging_submit = true;
      try {
        let response = await Requester.update(
          this.organization_id,
          this.harvest_process_id,
          this.harvest_team_work_log_id,
          this.harvest_team_work_log
        );

        this.registerSuccessNotification({
          title: response.data.success_message,
        });
        this.goToShowProcess();
      } catch (error) {
        console.log(error);
        if (error.response.status == 422) {
          this.loadStandardErrorMessages({
            title: error.response.data.error_title,
            messages: error.response.data.error_messages,
            fields: error.response.data.error_fields,
          });
        }
      }
      this.charging_submit = false;
    },
    goToShowProcess: function () {
      this.$router.push({
        name: this.routeName("show_process"),
        params: {
          organization_id: this.organization_id,
          harvest_process_id: this.harvest_process_id,
        },
      });
    },
  },
};
</script>
